import React, { useEffect, useMemo, useRef } from 'react';

const TitleIndex = ({
    titles = [],
    loading = false,
    error = null,
    activePostId = null,
    onSelectPostId,
}) => {
    const cardRef = useRef(null);
    const rafIdRef = useRef(null);

    useEffect(() => {
        const updateHeight = () => {
            const node = cardRef.current;
            if (!node) {
                return;
            }

            const rect = node.getBoundingClientRect();
            const top = Math.max(0, rect.top);
            const bottomGap = 16;
            const available = window.innerHeight - top - bottomGap;
            const height = Math.max(240, Math.floor(available));
            node.style.setProperty('--title-index-card-height', `${height}px`);
        };

        const scheduleUpdate = () => {
            if (rafIdRef.current != null) {
                return;
            }
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                updateHeight();
            });
        };

        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, { passive: true });

        return () => {
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate);
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, []);

    const sortedTitles = useMemo(() => {
        const safeTitles = Array.isArray(titles) ? titles : [];
        return [...safeTitles]
            .filter(item => item && item._id && typeof item.title === 'string')
            .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base', numeric: true }));
    }, [titles]);

    return (
        <aside className="title-index" aria-label="Article Title Index">
            <div className="title-index-card" ref={cardRef}>
                <div className="title-index-panel">
                    <h2>All Articles</h2>
                    {loading ? (
                        <p className="title-index-status">[INDEXING...]</p>
                    ) : error ? (
                        <p className="title-index-status">[SYSTEM ERROR]: {error}</p>
                    ) : sortedTitles.length === 0 ? (
                        <p className="title-index-status">[NO TITLES FOUND]</p>
                    ) : (
                        <div className="title-index-list" role="list">
                            {sortedTitles.map((item) => {
                                const isActive = Boolean(activePostId && String(item._id) === String(activePostId));
                                return (
                                    <button
                                        key={item._id}
                                        type="button"
                                        className={`title-index-item${isActive ? ' active' : ''}`}
                                        onClick={() => onSelectPostId?.(item._id)}
                                        title={item.title}
                                    >
                                        {item.title}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default TitleIndex;

